<script setup lang="ts">
import { computed } from "vue";

export type StepState = "pending" | "running" | "ok" | "failed" | "skipped";

export interface Step {
  label: string;
  state: StepState;
  /** Optional sub-status, e.g. "12s". */
  hint?: string;
}

const props = defineProps<{ steps: Step[]; currentIndex?: number }>();

const totalSteps = computed(() => props.steps.length);
</script>

<template>
  <ol class="space-y-1.5" role="list">
    <li
      v-for="(s, i) in steps"
      :key="i"
      class="flex items-center gap-3 text-sm"
      :aria-current="s.state === 'running' ? 'step' : undefined"
    >
      <span
        class="inline-flex h-4 w-4 items-center justify-center rounded-sm text-2xs font-medium"
        :class="{
          'bg-status-idle/15 text-fg-muted': s.state === 'pending' || s.state === 'skipped',
          'bg-status-provisioning/20 text-status-provisioning': s.state === 'running',
          'bg-status-ready/20 text-status-ready': s.state === 'ok',
          'bg-status-failed/20 text-status-failed': s.state === 'failed',
        }"
        aria-hidden="true"
      >
        <template v-if="s.state === 'ok'">&#10003;</template>
        <template v-else-if="s.state === 'failed'">&#10007;</template>
        <template v-else-if="s.state === 'running'">&#9670;</template>
        <template v-else>{{ i + 1 }}</template>
      </span>

      <span class="text-fg dark:text-fg-dark flex-1">{{ s.label }}</span>

      <span
        class="text-2xs"
        :class="{
          'text-fg-muted': s.state === 'pending' || s.state === 'skipped',
          'text-status-provisioning': s.state === 'running',
          'text-status-ready': s.state === 'ok',
          'text-status-failed': s.state === 'failed',
        }"
      >
        {{ s.state }}<span v-if="s.hint"> · {{ s.hint }}</span>
      </span>
    </li>
  </ol>
  <p class="sr-only">{{ totalSteps }} total steps</p>
</template>
